import { useState } from "react";
import { createAssociation, deleteAssociation, verifyAssociation } from "../services/associationApi.jsx";

interface Association {
  user_id: string;
  profile_id: string;
}

interface User {
  username: string;
  attributes: {
    email: string;
    email_verified?: boolean;
    sub: string;
  };
}

interface Profile {
  profile_id: string;
  backend_config?: {
    collections?: string[];
    prompts?: any[];
  };
}

interface AlertConfig {
  open: boolean;
  type: "success" | "error" | "loading" | "confirm";
  message: string;
  title: string;
  onConfirm: (() => Promise<void>) | undefined;
}

export const useAssociationActions = (associations: Association[], fetchAssociations: () => Promise<void>) => {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedProfiles, setSelectedProfiles] = useState<string[]>([]);

  const handleUserSelect = (user: User | null) => {
    if (user === null || user === undefined) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers([user.username]);
    }
  };

  const handleProfileSelect = (profile: Profile | null) => {
    if (profile === null || profile === undefined) {
      setSelectedProfiles([]);
    } else {
      setSelectedProfiles([profile.profile_id]);
    }
  };

  const handleClearSelections = () => {
    setSelectedUsers([]);
    setSelectedProfiles([]);
  };

  const hasExistingAssociation = (): boolean => {
    return associations.some(
      (assoc) =>
        assoc.user_id === selectedUsers[0] &&
        assoc.profile_id === selectedProfiles[0]
    );
  };

  const isUserAlreadyAssociated = (userId: string): boolean => {
    return associations.some(assoc => assoc.user_id === userId);
  };

  const getAssociatedProfile = (userId: string): string | null => {
    const association = associations.find(assoc => assoc.user_id === userId);
    return association ? association.profile_id : null;
  };

  const checkAssociation = async (userId: string): Promise<string | null> => {
    try {
      const result = await verifyAssociation(userId);
      return result && result.profile_id;
    } catch (error: any) {
      if (error.message && error.message.includes("404")) {
        return null;
      }
      throw error;
    }
  };

  const handleCreateAssociation = async (setAlertConfig: (config: AlertConfig) => void) => {
    if (selectedUsers.length === 0 || selectedProfiles.length === 0) {
      setAlertConfig({
        open: true,
        type: "error",
        message: "Debe seleccionar un usuario y un perfil para crear una asociación",
        title: "Error",
        onConfirm: undefined,
      });
      return;
    }
    
    if (hasExistingAssociation()) {
      setAlertConfig({
        open: true,
        type: "error",
        message: "Esta asociación ya existe",
        title: "Error",
        onConfirm: undefined,
      });
      return;
    }

    setAlertConfig({
      open: true,
      type: "loading",
      message: "Creando asociación...",
      title: "",
      onConfirm: undefined,
    });

    try {
      const newAssociation = await createAssociation({
        user_id: selectedUsers[0],
        profile_id: selectedProfiles[0],
      });

      await fetchAssociations();
      handleClearSelections();
      
      setAlertConfig({
        open: true,
        type: "success",
        message: "Asociación creada con éxito",
        title: "Éxito",
        onConfirm: undefined,
      });
    } catch (error: any) {
      console.error("Error al crear asociación:", error);

      let errorMessage = "Error al crear la asociación";
      if (error.message && error.message.includes("AlreadyAssociatedError")) {
        errorMessage = "El usuario ya está asociado a un perfil";
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.error_message
      ) {
        errorMessage = error.response.data.error_message;
      }

      setAlertConfig({
        open: true,
        type: "error",
        message: errorMessage,
        title: "Error",
        onConfirm: undefined,
      });
    }
  };

  const handleDeleteAssociation = async (userId: string, setAlertConfig: (config: AlertConfig) => void) => {
    try {
      setAlertConfig({
        open: true,
        type: "confirm",
        message: "¿Deseas eliminar esta asociación y forzar la desconexión del usuario?",
        title: "Confirmar acción",
        onConfirm: async () => {
          try {
            setAlertConfig({
              open: true,
              type: "loading",
              message: "Eliminando asociación...",
              title: "",
              onConfirm: undefined,
            });

            await deleteAssociation(userId);
            await fetchAssociations();

            setAlertConfig({
              open: true,
              type: "success",
              message: "Asociación eliminada con éxito. El usuario perderá acceso en su próxima solicitud o cuando su sesión expire.",
              title: "Éxito",
              onConfirm: undefined,
            });
          } catch (error) {
            console.error("Error al eliminar asociación:", error);
            setAlertConfig({
              open: true,
              type: "error",
              message: "Error al eliminar la asociación",
              title: "Error",
              onConfirm: undefined,
            });
          }
        },
      });
    } catch (error) {
      console.error("Error:", error);
    }
  };

  return {
    selectedUsers,
    selectedProfiles,
    handleUserSelect,
    handleProfileSelect,
    handleClearSelections,
    handleCreateAssociation,
    handleDeleteAssociation,
    hasExistingAssociation,
    isUserAlreadyAssociated,
    getAssociatedProfile,
    setSelectedUsers,
    setSelectedProfiles
  };
};